// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FontAwesomeDetectorService {
  private fontAwesomeLoadedSubject = new BehaviorSubject<boolean>(true);
  private checkPerformedSubject = new BehaviorSubject<boolean>(false);

  public fontAwesomeLoaded$: Observable<boolean> = this.fontAwesomeLoadedSubject.asObservable();
  public checkPerformed$: Observable<boolean> = this.checkPerformedSubject.asObservable();

  private readonly FONT_AWESOME_CDN_URL = 'https://kit.fontawesome.com/8f664a0601.js';

  constructor() {
    this.initializeDetection();
  }

  private initializeDetection(): void {
    // Wait for DOM to be ready and give Font Awesome some time to load
    timer(1000).subscribe(() => {
      this.checkFontAwesome();
    });
  }

  private async checkFontAwesome(): Promise<void> {
    try {
      // Create AbortController for timeout functionality
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      // Make fetch request to Font Awesome CDN to check if it's accessible
      const response = await fetch(this.FONT_AWESOME_CDN_URL, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        signal: controller.signal
      });
      
      // Clear timeout since request completed
      clearTimeout(timeoutId);
      
      const isLoaded = response.ok && response.status === 200;
      
      this.fontAwesomeLoadedSubject.next(isLoaded);
      this.checkPerformedSubject.next(true);
      
      if (!isLoaded) {
        console.warn('FontAwesome Detector: Font Awesome CDN is not accessible. Icons may not render properly.');
        console.warn('FontAwesome Detector: Response status:', response.status, response.statusText);
      }
      
    } catch (error: unknown) {
      // Handle different types of errors
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          console.warn('FontAwesome Detector: CDN request timed out');
        } else if (error.name === 'TypeError') {
          console.warn('FontAwesome Detector: Network error - likely CORS, SSL, or connectivity issue');
        } else if (error.name === 'NetworkError') {
          console.warn('FontAwesome Detector: Network connectivity issue');
        } else {
          console.warn('FontAwesome Detector: CDN request failed:', error.message);
        }
      } else {
        console.warn('FontAwesome Detector: Unknown error occurred while checking CDN');
      }
      
      this.fontAwesomeLoadedSubject.next(false);
      this.checkPerformedSubject.next(true);
    }
  }


  /**
   * Manually trigger a Font Awesome check
   */
  public recheckFontAwesome(): void {
    this.checkFontAwesome();
  }

  /**
   * Get current status without subscribing
   */
  public isFontAwesomeLoaded(): boolean {
    return this.fontAwesomeLoadedSubject.value;
  }

  /**
   * Check if the detection has been performed
   */
  public isCheckPerformed(): boolean {
    return this.checkPerformedSubject.value;
  }
}
